/**
 * The visibility-range cull of a scene's geometry instances, run once for each render of the
 * scene, before its render list and its shadow pass. Each render camera keeps its own hysteresis
 * state, as each Godot viewport does (`renderer_scene_cull.h:301`).
 */
import * as THREE from 'three';
import { acyclicParents, cullVisibility, } from '../../godot/rendering/visibilityCull';
import { NO_VISIBILITY_RANGE } from '../../godot/rendering/visibilityRange';
import { observeSceneCull } from '../sceneRenderCamera';
import { sceneSplitsHold } from '../directionalShadow/fitSceneDirectionalShadows';
import { compareTreeOrder } from '../contexts/TreeOrderContext';
import { cameraFrustum } from '../cameraFrustum';
/** Written only by `registerVisibilityInstance`. A scene leaves it with its last instance. */
// eslint-disable-next-line no-restricted-syntax -- a scene's last instance deletes its entry
const scenes = new WeakMap();
/** Adds `instance` to the cull of `scene` until the returned function runs. */
export function registerVisibilityInstance(scene, instance) {
    const registry = scenes.get(scene) ?? createRegistry(scene);
    registry.instances.set(instance, { links: instance.links, hasResult: false, wasVisible: new WeakMap() });
    registry.topology = null;
    return () => {
        registry.instances.delete(instance);
        registry.topology = null;
        if (registry.instances.size > 0)
            return;
        registry.stopObserving();
        scenes.delete(scene);
    };
}
function createRegistry(scene) {
    const registry = { instances: new Map(), topology: null, stopObserving: () => { } };
    registry.stopObserving = observeSceneCull(scene, (camera) => cullScene(scene, registry, camera));
    scenes.set(scene, registry);
    return registry;
}
/** Scratch, reused by every cull: three renders one scene at a time. */
const frustum = new THREE.Frustum();
const cameraPosition = new THREE.Vector3();
const box = new THREE.Box3();
const boxCentre = new THREE.Vector3();
function cullScene(scene, registry, camera) {
    const { members, parents, unculled } = currentTopology(registry);
    for (const instance of unculled)
        instance.apply(true, 1);
    if (members.length === 0)
        return;
    cameraFrustum(camera, frustum);
    cameraPosition.setFromMatrixPosition(camera.matrixWorld);
    const states = members.map((instance) => registry.instances.get(instance));
    const isMeasured = measuredMembers(members, parents);
    const inputs = members.map((instance, i) => {
        if (!isMeasured[i])
            return unmeasuredInput(parents[i]);
        instance.worldBox(box);
        return {
            range: instance.range,
            isIndexed: instance.isIndexed,
            parent: parents[i],
            distance: cameraPosition.distanceTo(box.getCenter(boxCentre)),
            isInView: frustum.intersectsBox(box) || sceneSplitsHold(scene, box),
            wasVisible: states[i].wasVisible.get(camera) ?? false,
        };
    });
    cullVisibility(inputs).forEach((result, i) => {
        const state = states[i];
        if (!isMeasured[i]) {
            if (!state.hasResult)
                members[i].apply(false, 1);
            return;
        }
        state.hasResult = true;
        state.wasVisible.set(camera, result.wasVisible);
        members[i].apply(result.isVisible, result.fade);
    });
}
/**
 * Whether the cull can measure each member: it is placed, and so is each visibility parent above
 * it along the links Godot keeps. A dependant's result reads its parent's.
 */
function measuredMembers(members, parents) {
    const isPlaced = members.map((instance) => instance.isPlaced);
    return members.map((_, i) => {
        for (let at = i; at >= 0; at = parents[at])
            if (!isPlaced[at])
                return false;
        return true;
    });
}
/** A stand-in for a member the cull cannot measure: it keeps its links, and its result is dropped. */
function unmeasuredInput(parent) {
    return {
        range: NO_VISIBILITY_RANGE,
        isIndexed: false,
        parent,
        distance: 0,
        isInView: false,
        wasVisible: false,
    };
}
/** The registry's topology, rebuilt when an instance joined, left or relinked since the last cull. */
function currentTopology(registry) {
    for (const [instance, state] of registry.instances) {
        if (instance.links === state.links)
            continue;
        state.links = instance.links;
        registry.topology = null;
    }
    registry.topology ??= buildTopology(inTreeOrder(registry.instances.keys()));
    return registry.topology;
}
/** Stable, so instances outside the dispatcher, which tie at the root, keep registration order. */
function inTreeOrder(instances) {
    return [...instances].sort((a, b) => compareTreeOrder(a.links.order, b.links.order));
}
/**
 * The instances the cull must measure, those with a range, a parent or a dependant, each with its
 * parent's index, from `instances` in tree order. A parent path that names no registered instance
 * is no parent.
 */
function buildTopology(instances) {
    const byPath = new Map();
    for (const instance of instances)
        if (instance.links.path !== null)
            byPath.set(instance.links.path, instance);
    const parentOf = new Map();
    for (const instance of instances) {
        const { parentPath, hasRange } = instance.links;
        const parent = parentPath === null ? undefined : byPath.get(parentPath);
        if (parent) {
            parentOf.set(instance, parent);
            if (!parentOf.has(parent))
                parentOf.set(parent, null);
        }
        else if (hasRange && !parentOf.has(instance)) {
            parentOf.set(instance, null);
        }
    }
    // In tree order, not `parentOf`'s, which takes a parent before its own turn: the cull refuses
    // the later link that closes a cycle.
    const members = instances.filter((instance) => parentOf.has(instance));
    const indexOf = new Map(members.map((instance, i) => [instance, i]));
    return {
        members,
        parents: acyclicParents(members.map((instance) => {
            const parent = parentOf.get(instance);
            return parent ? indexOf.get(parent) : -1;
        })),
        unculled: instances.filter((instance) => !parentOf.has(instance)),
    };
}
//# sourceMappingURL=visibilityScene.js.map